import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, MessageSquareWarning, Printer, RefreshCw, Search } from 'lucide-react';
import { complaintAPI } from '../../services/api';
import { useToast } from '../UIProvider';
import { formatClinicDate, formatClinicDateTime } from '../../utils/clinicTime';
import { onActivateKey, ROW_FOCUS_RING } from '../../utils/keyboard';
import Select from '../Select';
import TablePager, { usePagedRows } from '../TablePager';
import ComplaintDetailModal from './ComplaintDetailModal';
import ComplaintPoster from './ComplaintPoster';
import ComplaintsOverview from './ComplaintsOverview';
import { CATEGORY_KEYS, SEVERITY, STATUS, categoryLabel, isOverdue, isWrittenPage, messageOf, statusOf } from './complaintStatus';

// What patients sent in from the poster on the wall, and what has been done about each one. New ones
// lead, because the point of the module is that nothing sits unseen: the sidebar carries the same
// count and it only drops when somebody actually picks the complaint up.
//
// With `mine`, this is the assignee's view: the complaints handed to them and nothing else. Somebody
// can be asked to look into one complaint without being given the run of the module, so this mode
// needs no complaints permission at all — the server decides what it returns.

const STATUS_FILTERS = [
  { key: 'NEW', label: 'New' },
  { key: 'UNDER_REVIEW', label: 'Under review' },
  { key: 'AWAITING_COMPLAINANT', label: 'Waiting on them' },
  { key: 'RESOLVED', label: 'Resolved' },
  { key: 'DISMISSED', label: 'Dismissed' },
  { key: 'ALL', label: 'All' },
];

const NARROW = [
  { key: '', label: 'Everyone' },
  { key: 'me', label: 'Mine' },
  { key: 'nobody', label: 'Unassigned' },
  { key: 'overdue', label: 'Overdue' },
];

const ComplaintsManagement = ({ hasPermission, mine = false, defaultTab }) => {
  // The sidebar pins the view through defaultTab, as Expenses and Reports do; `mine` stays a
  // prop of its own so the two modes are testable without routing.
  const isMine = mine || defaultTab === 'mine';
  const notify = useToast();
  const can = useCallback(
    (action) => (typeof hasPermission === 'function' ? hasPermission('COMPLAINTS', action) : true),
    [hasPermission],
  );
  const canSeeAll = can('VIEW');

  const [tab, setTab] = useState('list');
  const [status, setStatus] = useState(isMine ? 'ALL' : 'NEW');
  const [category, setCategory] = useState('ALL');
  const [narrow, setNarrow] = useState('');
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [poster, setPoster] = useState(false);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const res = isMine
        ? await complaintAPI.mine()
        : await complaintAPI.getAll({
            status,
            category,
            assigned: narrow === 'me' || narrow === 'nobody' ? narrow : undefined,
            overdue: narrow === 'overdue' ? true : undefined,
          });
      setRows(Array.isArray(res) ? res : []);
      setError(null);
    } catch (e) {
      setError(e?.message || 'The complaints could not be loaded.');
    }
  }, [isMine, status, category, narrow]);
  useEffect(() => { load(); }, [load]);

  // The badge in the sidebar counts the same thing this page shows, so it has to be told when a
  // complaint is picked up here.
  const refreshBadge = () => window.dispatchEvent(new Event('clinic:complaints-changed'));

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = rows || [];
    const byStatus = isMine && status !== 'ALL' ? list.filter((c) => c.status === status) : list;
    if (!q) return byStatus;
    return byStatus.filter((c) => [c.reference, c.complainantName, c.phone, c.message, c.assignedToName, categoryLabel(c)]
      .some((v) => String(v || '').toLowerCase().includes(q)));
  }, [rows, query, isMine, status]);

  const counts = useMemo(() => ({ shown: shown.length, total: (rows || []).length }), [rows, shown]);
  const paging = usePagedRows(shown, { id: 'complaints', resetOn: [status, category, narrow, query] });

  const emptyLine = () => {
    if (query) return 'Nothing matches that.';
    if (isMine) return 'Nothing is assigned to you.';
    if (narrow === 'overdue') return 'Nothing is overdue.';
    if (narrow === 'nobody') return 'Everything has somebody looking at it.';
    if (status === 'NEW') return 'No new complaints.';
    return 'Nothing here.';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6 print:bg-white print:p-0">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 print:hidden">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center">
                <MessageSquareWarning className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{isMine ? 'My complaints' : 'Complaints'}</h1>
                <p className="text-gray-600">
                  {isMine
                    ? 'Complaints somebody has asked you to look into'
                    : 'What patients sent in from the poster on the wall'}
                </p>
              </div>
            </div>
            {!isMine && canSeeAll && (
              <button type="button" onClick={() => setPoster(true)} className="flex items-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">
                <Printer className="h-4 w-4" aria-hidden="true" /> Poster for the wall
              </button>
            )}
          </div>

          {!isMine && canSeeAll && (
            <div className="flex gap-2 mt-4" role="group" aria-label="View">
              {[{ key: 'list', label: 'Complaints' }, { key: 'overview', label: 'Overview' }].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  aria-pressed={tab === t.key}
                  onClick={() => setTab(t.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === t.key ? 'bg-navy-800 text-white' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {tab === 'overview' && !isMine ? (
          <ComplaintsOverview />
        ) : (
          <>
            <div className="bg-white rounded-2xl shadow-xl p-5 mb-6 space-y-3 print:hidden">
              <div className="flex gap-2 flex-wrap" role="group" aria-label="Status">
                {STATUS_FILTERS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    aria-pressed={status === f.key}
                    onClick={() => setStatus(f.key)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${status === f.key ? 'bg-navy-800 text-white' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {!isMine && (
                <div className="flex gap-2 flex-wrap" role="group" aria-label="Narrow by">
                  {NARROW.map((f) => (
                    <button
                      key={f.key || 'all'}
                      type="button"
                      aria-pressed={narrow === f.key}
                      onClick={() => setNarrow(f.key)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium ${narrow === f.key ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              )}

              <div className="flex gap-2 flex-wrap items-end">
                <div className="relative max-w-sm flex-1 min-w-[220px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" aria-hidden="true" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search the reference, name, number or words in the complaint"
                    aria-label="Search complaints"
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                {!isMine && (
                  <div className="min-w-[200px]">
                    <Select
                      label="Category"
                      value={category}
                      onChange={setCategory}
                      options={[
                        { value: 'ALL', label: 'Every category' },
                        ...CATEGORY_KEYS.map((k) => ({ value: k, label: categoryLabel({ category: k }) })),
                      ]}
                    />
                  </div>
                )}
                <button type="button" onClick={load} aria-label="Refresh" className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                  <RefreshCw className={`w-4 h-4 text-gray-600 ${rows === null ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {error ? (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg text-sm text-red-800">{error}</div>
            ) : rows === null ? (
              <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                <RefreshCw className="h-6 w-6 text-emerald-500 animate-spin mx-auto mb-2" />
                <p className="text-sm text-gray-500">Loading complaints…</p>
              </div>
            ) : shown.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                <MessageSquareWarning className="h-10 w-10 text-gray-300 mx-auto mb-3" aria-hidden="true" />
                <p className="font-semibold text-gray-900">{emptyLine()}</p>
                {!query && !isMine && status === 'NEW' && !narrow && (
                  <p className="text-sm text-gray-500 mt-1">Complaints sent from the poster appear here as soon as they arrive.</p>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-xl">
                <TablePager paging={paging} noun="complaint" filteredFrom={counts.total} className="px-5" />
                <table className="w-full table-fixed text-sm">
                  <colgroup>
                    <col className="w-40" />
                    <col className="w-28" />
                    <col />
                    <col className="w-40" />
                    <col className="w-36" />
                    <col className="w-36" />
                  </colgroup>
                  <thead>
                    <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                      <th className="px-5 py-3">Reference</th>
                      <th className="px-4 py-3">About</th>
                      <th className="px-4 py-3">Complaint</th>
                      <th className="px-4 py-3">From</th>
                      <th className="px-4 py-3">With</th>
                      <th className="px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paging.pageRows.map((c) => {
                      const openRow = () => setSelected(c);
                      const chip = statusOf(c);
                      const late = isOverdue(c);
                      return (
                        <tr key={c.id} tabIndex={0} onClick={openRow} onKeyDown={onActivateKey(openRow)} className={`cursor-pointer hover:bg-gray-50 ${ROW_FOCUS_RING}`}>
                          <td className="px-5 py-3">
                            <div className="font-mono text-xs text-gray-900">{c.reference}</div>
                            <div className="text-xs text-gray-500">{formatClinicDateTime(c.createdAt)}</div>
                          </td>
                          <td className="px-4 py-3 text-gray-700">{categoryLabel(c)}</td>
                          <td className="px-4 py-3 text-gray-700 truncate" dir="auto" title={messageOf(c)}>
                            {messageOf(c) || (
                              <span className="italic text-gray-500">
                                {isWrittenPage(c) ? 'Written complaint attached' : 'Nothing written'}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-gray-900 truncate" dir="auto">{c.complainantName}</div>
                            <div className="text-xs text-gray-500">{c.phone}</div>
                          </td>
                          <td className="px-4 py-3">
                            {c.assignedToName ? (
                              <span className="text-gray-900 truncate block">{c.assignedToName}</span>
                            ) : (
                              <span className="text-gray-400">Nobody</span>
                            )}
                            {c.dueAt && !['RESOLVED', 'DISMISSED'].includes(c.status) && (
                              <span className={`block text-xs ${late ? 'text-red-700 font-medium' : 'text-gray-500'}`}>
                                due {formatClinicDate(c.dueAt, { day: 'numeric', month: 'short' })}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${chip.chip}`}>{chip.label}</span>
                            <span className="block mt-1 space-x-1">
                              {c.severity && (
                                <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${SEVERITY[c.severity].chip}`}>
                                  {SEVERITY[c.severity].label}
                                </span>
                              )}
                              {late && (
                                <span className="inline-flex items-center gap-0.5 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-800">
                                  <AlertTriangle className="w-3 h-3" aria-hidden="true" /> Overdue
                                </span>
                              )}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <TablePager paging={paging} noun="complaint" filteredFrom={counts.total} position="bottom" className="px-5" />
              </div>
            )}
          </>
        )}
      </div>

      {selected && (
        <ComplaintDetailModal
          complaint={selected}
          canEdit={can('EDIT')}
          canDelete={can('DELETE')}
          canAssign={can('ASSIGN_COMPLAINT')}
          // Deliberately stays open: having written why a complaint is being closed, the person
          // should see it take effect rather than have the record snatched away. The list behind it
          // and the sidebar count refresh meanwhile.
          onChanged={() => { refreshBadge(); load(); }}
          onDeleted={() => { setSelected(null); refreshBadge(); load(); notify('success', 'Deleted'); }}
          onClose={() => setSelected(null)}
        />
      )}
      {poster && <ComplaintPoster onClose={() => setPoster(false)} />}
    </div>
  );
};

export default ComplaintsManagement;
